import { useTranslation } from 'react-i18next'
import ExportButtons from '../../../components/ExportButtons'
import { useMineMerker } from '../../../hooks/useMineMerker'
import type { ColumnDefinition, ExportMetadata } from '../../../utils/exportToCsv'
import type { SammenligningRow } from './api'

interface Props {
  rows: SammenligningRow[]
  /** Context for the CSV/Excel/PDF export (period, active filters). */
  exportMeta?: ExportMetadata
}

/**
 * Comparison grid: one row per merke × overdragelsesklasse pair, aggregated
 * over the whole sales period. Exportable to CSV/Excel/PDF.
 */
export default function SammenligningGrid({ rows, exportMeta }: Props) {
  const { t } = useTranslation('bruktbil')
  const { activeMerker } = useMineMerker()

  const exportColumns: ColumnDefinition[] = [
    { key: 'merke', label: t('common.merke') },
    { key: 'klasse', label: t('common.klasse') },
    {
      key: 'eierskifter',
      label: t('common.eierskifter'),
      format: (v: number) => v.toLocaleString('nb-NO'),
    },
    {
      key: 'snittalder',
      label: t('merkeModell.sammenligningGrid.snittalderBil'),
      format: (v: number | null) =>
        v == null ? '' : v.toLocaleString('nb-NO'),
    },
  ]

  if (rows.length === 0) {
    return (
      <div className="card p-4 text-sm text-gray-500">
        {t('merkeModell.sammenligningGrid.velgMerke')}
      </div>
    )
  }

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('merkeModell.sammenligningGrid.title')}
        </span>
        <ExportButtons
          data={rows}
          columns={exportColumns}
          metadata={exportMeta ?? { title: t('merkeModell.sammenligningExportTitle') }}
        />
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
            <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('common.merke')}</th>
            <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('common.klasse')}</th>
            <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.eierskifter')}</th>
            <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('merkeModell.sammenligningGrid.snittalderBil')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={`${row.merke ?? ''}-${row.klasse ?? ''}-${i}`}
              className={`border-b border-gray-100 dark:border-dark-border ${row.merke && activeMerker.has(row.merke) ? 'bg-primary-100 dark:bg-primary-900/30 border-l-4 border-primary-500' : ''}`}
            >
              <td className="px-4 py-2">{row.merke ?? '—'}</td>
              <td className="px-4 py-2">{row.klasse ?? '—'}</td>
              <td className="px-4 py-2 text-right tabular-nums">
                {row.eierskifter.toLocaleString('nb-NO')}
              </td>
              <td className="px-4 py-2 text-right tabular-nums">
                {row.snittalder == null ? '—' : row.snittalder.toLocaleString('nb-NO')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
